import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { Building2 } from 'lucide-react'
import { Modal } from 'components/UI/Modal/Modal'
import FbButton from 'components/FbUI/FbButton'
import { DataTableProvider } from 'components/DataTable'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import { UnifiedSearchInput } from 'components/UnifiedSearch/UnifiedSearchInput'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { UnifiedSearchFilters } from 'components/UnifiedSearch/UnifiedSearchFilters'
import { MIN_SEARCH_CHARS } from 'components/UnifiedSearch/constants'
import { UnifiedSearchResults } from 'components/UnifiedSearch/UnifiedSearchResults'
import {
  useUnifiedSearchState,
  useUnifiedSearchAPI,
  useUnifiedSearchOptions,
} from 'components/UnifiedSearch/hooks'
import { ContactCompanySearchResult, DoorSearchResult } from 'models/companies'
import { toast } from 'react-toastify'
import useApiService from 'services/api'

const PAGE_SIZE = 20

interface CompanySearchModalProps {
  open: boolean
  onClose: () => void
  onSelectCompany: (
    company: ContactCompanySearchResult,
    door?: DoorSearchResult
  ) => void
  /** When provided, only these tabs are shown in the search results. */
  visibleTabs?: Array<
    | 'mycompanies'
    | 'firstbite'
    | 'unmatched'
    | 'firstbitedoors'
    | 'unmatcheddoors'
    | 'variants'
  >
}

export function CompanySearchModal({
  open,
  onClose,
  onSelectCompany,
  visibleTabs,
}: CompanySearchModalProps) {
  const api = useApiService()
  const [selectedCompany, setSelectedCompany] =
    useState<ContactCompanySearchResult | null>(null)
  const [selectedDoor, setSelectedDoor] = useState<DoorSearchResult | null>(
    null
  )
  // Track the originally clicked row for radio button selection
  const [selectedRow, setSelectedRow] = useState<any>(null)
  // Track loading state for company resolution
  const [isResolvingCompany, setIsResolvingCompany] = useState(false)

  // Memoize initial state to prevent re-creating on every render
  const initialState = useMemo(
    () => ({
      searchTerm: '',
      selectedTab: (visibleTabs?.[0] ?? 'firstbite') as
        | 'mycompanies'
        | 'firstbite'
        | 'unmatched'
        | 'firstbitedoors'
        | 'unmatcheddoors'
        | 'variants'
        | 'deals',
      filters: {
        category: 'all',
        type: 'all',
        product: 'all',
      },
      sortBy: 'relevance',
      firstBitePage: 1,
      unmatchedPage: 1,
      firstBiteDoorPage: 1,
      unmatchedDoorPage: 1,
      variantPage: 1,
      dealPage: 1,
    }),
    []
  )

  // Search state management
  const searchState = useUnifiedSearchState({
    initialState,
  })

  // Search options (categories, types, sort, products)
  const {
    categoryOptions,
    typeOptions,
    sortOptions,
    productOptions,
    isOppLoading,
    isProductsLoading,
  } = useUnifiedSearchOptions({
    filters: searchState.filters,
  })

  // API and search results
  const { clearResults, ...searchResults } = useUnifiedSearchAPI({
    searchTerm: searchState.debouncedSearchTerm,
    filters: searchState.filters,
    sortBy: searchState.sortBy,
    firstBitePage: searchState.firstBitePage,
    unmatchedPage: searchState.unmatchedPage,
    firstBiteDoorPage: searchState.firstBiteDoorPage,
    unmatchedDoorPage: searchState.unmatchedDoorPage,
    variantPage: searchState.variantPage,
    dealPage: searchState.dealPage,
    selectedTab: searchState.selectedTab,
    pageSize: PAGE_SIZE,
    myCompaniesEnabled: visibleTabs?.includes('mycompanies') ?? false,
  })

  // Use the originally clicked row for radio button selection
  const selectedSearchResult = selectedRow

  // Handle company/door row click (variants tab not supported - this modal is for company/door selection)
  const handleCompanyClick = useCallback(
    async (companyOrDoor: any) => {
      // Variants tab is not supported in CompanySearchModal - it's for company/door selection only
      if (searchState.selectedTab === 'variants') {
        return
      }

      // Set selected row immediately for instant UI feedback
      setSelectedRow(companyOrDoor)

      if (searchState.selectedTab === 'mycompanies') {
        // User's custom (unmatched) companies — row already carries contact_company_id.
        setSelectedCompany(companyOrDoor as ContactCompanySearchResult)
        setSelectedDoor(null)
      } else if (searchState.selectedTab === 'firstbite') {
        // FirstBite company selection - need to resolve from item_id to contact_company_id
        const chainResult = companyOrDoor as any

        // The contact_company_id in the result is actually parsed from item_id (incorrectly)
        // We need to use the fake contact_company_id as the chain_id
        const chainId = chainResult.contact_company_id

        if (!chainId || isNaN(chainId)) {
          toast.error('Invalid company ID format', { autoClose: 5000 })
          setSelectedRow(null)
          return
        }

        setIsResolvingCompany(true)
        try {
          const resolved = await api.resolveCompanyFromFbIds({
            chainId,
            type: chainResult.type,
          })

          const companyData: ContactCompanySearchResult = {
            contact_company_id: resolved.contact_company_id,
            name: resolved.name,
            domain: chainResult.domain || '',
            city: resolved.city || chainResult.city || undefined,
            state: resolved.state || chainResult.state || undefined,
            address_line1: resolved.address_line1 ?? undefined,
            zipcode: resolved.zipcode ?? undefined,
          }

          setSelectedCompany(companyData)
          setSelectedDoor(null)
        } catch (error: any) {
          toast.error(
            error?.response?.data?.error ||
              'Unable to find this company in your contacts. Please add it first.',
            { autoClose: 5000 }
          )
          setSelectedRow(null)
          setSelectedCompany(null)
          setSelectedDoor(null)
          return
        } finally {
          setIsResolvingCompany(false)
        }
      } else if (searchState.selectedTab === 'unmatched') {
        // Unmatched company selection (already has contact_company_id)
        setSelectedCompany(companyOrDoor as ContactCompanySearchResult)
        setSelectedDoor(null)
      } else if (searchState.selectedTab === 'firstbitedoors') {
        // FirstBite door selection - need to resolve contact_company_id
        const door = companyOrDoor as DoorSearchResult

        if (!door.chain_id || !door.type) {
          toast.error(
            'This location cannot be selected because it is missing chain information.',
            { autoClose: 5000 }
          )
          setSelectedRow(null)
          return
        }

        setIsResolvingCompany(true)
        try {
          const resolved = await api.resolveCompanyFromFbIds({
            chainId: door.chain_id,
            doorId: door.id,
            type: door.type,
          })

          // Store door_proxy_id on the door object so it can be used later
          const doorWithProxyId = {
            ...door,
            door_proxy_id: resolved.door_proxy_id,
          }
          setSelectedDoor(doorWithProxyId)

          const companyData: ContactCompanySearchResult = {
            contact_company_id: resolved.contact_company_id,
            name: door.chain_name || resolved.name,
            domain: door.domain || '',
            city: resolved.city || door.city || undefined,
            state: resolved.state || door.state || undefined,
            address_line1: resolved.address_line1 || door.address || undefined,
            zipcode: resolved.zipcode || door.zipcode || undefined,
          }

          setSelectedCompany(companyData)
        } catch (error: any) {
          toast.error(
            error?.response?.data?.error ||
              'Unable to find this company in your contacts. Please add it first.',
            { autoClose: 5000 }
          )
          setSelectedRow(null)
          setSelectedCompany(null)
          setSelectedDoor(null)
          return
        } finally {
          setIsResolvingCompany(false)
        }
      } else if (searchState.selectedTab === 'unmatcheddoors') {
        // Unmatched door selection (already has chain_proxy_id)
        const door = companyOrDoor as DoorSearchResult

        // Use chain_proxy_id (ContactCompany ID) if available,
        // otherwise use chain_id (will be resolved on backend)
        if (!door.chain_proxy_id && !door.chain_id) {
          toast.error(
            'This location cannot be selected because it is not associated with a company. ' +
              'Please select a different location or use the company search tab.',
            { autoClose: 5000 }
          )
          setSelectedRow(null)
          return
        }

        setSelectedDoor(door)

        // Create a ContactCompanySearchResult from the door's chain proxy info
        // If chain_proxy_id exists, use it; otherwise pass chain_id to be resolved on backend
        const companyData = {
          contact_company_id: door.chain_proxy_id || undefined,
          chain_id: door.chain_id,
          name: door.chain_name || `Location ${door.id}`,
          domain: door.domain || '',
          city: door.city || undefined,
          state: door.state || undefined,
        } as ContactCompanySearchResult & { chain_id?: number }

        setSelectedCompany(companyData)
      }
    },
    [searchState.selectedTab, api]
  )

  // Handle form submission (enter key)
  const handleSearchSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  // Handle select button click
  const handleSelectClick = useCallback(() => {
    if (selectedCompany) {
      onSelectCompany(selectedCompany, selectedDoor || undefined)
      setSelectedCompany(null)
      setSelectedDoor(null)
      setSelectedRow(null)
      onClose()
    }
  }, [selectedCompany, selectedDoor, onSelectCompany, onClose])

  // Handle modal close
  const handleClose = useCallback(() => {
    setSelectedCompany(null)
    setSelectedDoor(null)
    setSelectedRow(null)
    onClose()
    clearResults()
  }, [onClose, clearResults])

  // Reset search when modal closes
  useEffect(() => {
    if (!open) {
      searchState.setSearchTerm('')
      searchState.resetPagination()
    }
  }, [open, searchState])

  const selectedSubline = selectedDoor
    ? [
        `Location: ${selectedDoor.name}`,
        selectedDoor.address || null,
        [selectedDoor.city, selectedDoor.state].filter(Boolean).join(', ') ||
          null,
      ]
        .filter(Boolean)
        .join(' — ')
    : selectedCompany?.city && selectedCompany?.state
      ? `${selectedCompany.city}, ${selectedCompany.state}`
      : null

  return (
    <Modal
      open={open}
      onOpenChange={(open) => !open && handleClose()}
      size="2xl"
      title={
        visibleTabs?.length === 1 && visibleTabs[0] === 'mycompanies'
          ? 'My Companies'
          : 'Search for Company'
      }
      hideCancelButton
      dialogFooterClassName="hidden"
      childrenContainerClassName="max-h-[80vh] overflow-y-auto overflow-x-auto max-w-full w-full"
      footerChildren={
        <div className="flex items-center justify-between gap-4 border-t border-line pt-3">
          <div className="min-w-0 flex-1">
            {isResolvingCompany ? (
              <div className="flex items-center gap-2">
                <div className="size-4 animate-pulse rounded bg-surface-alt-active" />
                <div className="h-4 w-40 animate-pulse rounded bg-surface-alt-active" />
              </div>
            ) : selectedCompany ? (
              <div className="flex items-center gap-2 text-sm">
                <Building2
                  className="size-4 shrink-0 text-success"
                  strokeWidth={2}
                />
                <span className="shrink-0 font-medium text-fg">
                  {selectedCompany.name}
                  {selectedDoor && (
                    <span className="ml-1 text-xs font-normal text-fg-secondary">
                      (Company Level)
                    </span>
                  )}
                </span>
                {selectedSubline && (
                  <span className="truncate text-fg-secondary">
                    · {selectedSubline}
                  </span>
                )}
              </div>
            ) : (
              <span className="text-sm text-fg-secondary">
                No company selected
              </span>
            )}
          </div>
          <div className="flex shrink-0 gap-2">
            <FbButton
              type="button"
              variant="secondary"
              size="md"
              onClick={handleClose}
              disabled={isResolvingCompany}
            >
              Cancel
            </FbButton>
            <FbButton
              type="button"
              size="md"
              onClick={handleSelectClick}
              disabled={!selectedCompany || isResolvingCompany}
              loading={isResolvingCompany}
            >
              Select company
            </FbButton>
          </div>
        </div>
      }
    >
      <div className="flex flex-col h-full">
        <div className="flex-1 py-2 px-0.5 space-y-4">
          {/* Search Input */}
          <TableLinkStyleProvider variant="plain">
            <UnifiedSearchInput
              searchTerm={searchState.searchTerm}
              setSearchTerm={searchState.setSearchTerm}
              onSubmit={handleSearchSubmit}
              searchType={searchResults.searchType}
              isLoading={searchResults.isLoading}
            />
          </TableLinkStyleProvider>

          <hr className="mt-3 border-gray-400" />

          {/* Tabs and Results */}
          <div className="[&_tr:has(input[type=radio]:checked)]:!bg-surface-selected [&_tr:has(input[type=radio]:checked):hover]:!bg-surface-active">
            <ColumnsStoreProvider
              tableKey="company-search-modal"
              savedPresetsIdentifier={'COMPANY_SEARCH_MODAL' as any}
            >
              <DataTableProvider tableKey="company-search-modal">
                <UnifiedSearchResults
                  selectedTab={searchState.selectedTab}
                  setSelectedTab={searchState.setSelectedTab}
                  firstBiteCount={searchResults.firstBiteCount}
                  unmatchedCount={searchResults.unmatchedCount}
                  firstBiteDoorCount={searchResults.firstBiteDoorCount}
                  unmatchedDoorCount={searchResults.unmatchedDoorCount}
                  variantCount={searchResults.variantCount}
                  dealCount={searchResults.dealCount}
                  myCompaniesCount={searchResults.myCompaniesCount}
                  myCompaniesResults={searchResults.myCompaniesResults}
                  myCompaniesLoading={searchResults.myCompaniesLoading}
                  variantResults={searchResults.variantResults}
                  dealResults={searchResults.dealResults}
                  currentResults={searchResults.currentResults}
                  debouncedSearchTerm={searchState.debouncedSearchTerm}
                  isLoading={searchResults.isLoading}
                  currentTotalRows={searchResults.currentTotalRows}
                  pageSize={PAGE_SIZE}
                  currentPage={searchState.currentPage}
                  setCurrentPage={searchState.setCurrentPage}
                  onRowClick={handleCompanyClick}
                  onDealRowClick={() => {}}
                  selectedItem={selectedSearchResult}
                  showRadioButtons={true}
                  onVariantDataChange={searchResults.refetchVariants}
                  visibleTabs={visibleTabs}
                  filtersSlot={
                    <UnifiedSearchFilters
                      filters={searchState.filters}
                      setFilters={searchState.setFilters}
                      sortBy={searchState.sortBy}
                      setSortBy={searchState.setSortBy}
                      categoryOptions={categoryOptions}
                      typeOptions={typeOptions}
                      sortOptions={sortOptions}
                      productOptions={productOptions}
                      isOppLoading={isOppLoading}
                      isProductsLoading={isProductsLoading}
                      selectedTab={searchState.selectedTab}
                      disabled={
                        !searchState.debouncedSearchTerm ||
                        searchState.debouncedSearchTerm.length <
                          MIN_SEARCH_CHARS
                      }
                    />
                  }
                />
              </DataTableProvider>
            </ColumnsStoreProvider>
          </div>
        </div>
      </div>
    </Modal>
  )
}
